import { Badge, Icon, loginWithPasskey, Spinner, TrustRow, useAsyncAction, type IconName } from '@pryme/ui';
import { Link } from 'react-router-dom';
import { ErrorBox } from '../components/ErrorBox';
import { environments, userPortalUrl } from '../lib/portal';
import { LocalTag } from '../components/OneTimeSecretPanel';

const CAPABILITIES: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'shieldCheck',
    title: 'Verified identity',
    text: 'People verify once with national eID (MitID through Idura) and bind a passkey. Your service receives proof of a real person, with its assurance level, environment and source.',
  },
  {
    icon: 'eyeOff',
    title: 'Pairwise privacy',
    text: 'Your service receives its own relationship identifier and only the facts the person approves, such as age.over18 = true. Never a birth date, national ID number, handle or the person’s other services. Everything else Pryme holds about the person stays confidential.',
  },
  {
    icon: 'agent',
    title: 'Agent authorization',
    text: 'Agents are principals with their own keys. People grant them scoped, expiring mandates; anything outside a mandate goes to the person for a passkey-confirmed approval.',
  },
  {
    icon: 'document',
    title: 'Electronic delegation',
    text: 'Mandates are signed by the person’s pairwise key, auditable, narrowable and revocable. For approved purchases Pryme issues AP2 checkout and payment mandates.',
  },
  {
    icon: 'signature',
    title: 'Machine-to-machine trust',
    text: 'Two independent Ed25519 keypairs per service. Every request is signed over method, path, body digest, audience, timestamp and a single-use nonce; Pryme signs every successful response.',
  },
  {
    icon: 'globe',
    title: 'European by design',
    text: 'Identity data is kept in per-person identity stores in the EU. Sandbox and production are separate deployments with separate trust roots.',
  },
];

const STEPS = [
  { title: 'Create a Pryme ID', text: 'In sandbox, verify with a test identity and bind a passkey. It is also your developer sign-in.' },
  { title: 'Register a service', text: 'Create an organization, name the service and register exact redirect URIs.' },
  { title: 'Issue credentials', text: 'Pryme generates a secret signing key for your backend, shown once, and gives you its verification key.' },
  { title: 'Integrate and activate', text: 'Add @pryme/sdk, sign the activation challenge, and accept “Continue with Pryme” sign-ins.' },
];

export function SignIn(props: { onSignedIn(): void; invite?: boolean }) {
  const login = useAsyncAction();
  const envs = environments();
  const sandbox = envs.find((e) => e.id === 'sandbox');
  const production = envs.find((e) => e.id === 'production');
  const host = (url?: string) => (url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : undefined);

  const signIn = async () => {
    const r = await login.run(() => loginWithPasskey());
    if (r) props.onSignedIn();
  };

  return (
    <>
      <section className="p-hero" aria-labelledby="dv-hero-title">
        <div className="p-hero-inner dv-hero-grid">
          <div>
            <span className="p-eyebrow">Pryme for developers</span>
            <h1 id="dv-hero-title">Verified people. Authorized agents. Trusted services.</h1>
            <p className="p-lead">
              Pryme gives your service eID-backed proof of a real person without your service having to collect or store their personal data, and lets software agents act for people within limits
              they approve. One signed protocol, pairwise by design, with identity data stored in the EU.
            </p>
            <div className="p-row">
              <Link className="p-btn large" to="/docs">Read the documentation</Link>
              <a className="p-btn large secondary" href={userPortalUrl('/enroll')}>Create a Pryme ID</a>
            </div>
            <TrustRow
              items={[
                { icon: 'eyeOff', label: 'Pairwise identifiers' },
                { icon: 'key', label: 'Passkey-confirmed decisions' },
                { icon: 'signature', label: 'Signed requests and responses' },
                { icon: 'globe', label: 'Identity data stored in the EU' },
              ]}
            />
          </div>
          <aside className="dv-signin" aria-labelledby="dv-signin-title">
            {props.invite && (
              <p className="p-alert info" role="status">
                <strong>Sign in to accept your team invitation.</strong>
              </p>
            )}
            <h2 id="dv-signin-title">Sign in</h2>
            <p className="p-small p-muted">Use the passkey of your Pryme ID. The developer site is open to people with an active, verified Pryme ID.</p>
            <button type="button" className="p-btn large" disabled={login.busy} onClick={() => void signIn()}>
              {login.busy ? <Spinner label="Waiting for your passkey…" /> : 'Sign in with passkey'}
            </button>
            <ErrorBox error={login.error} />
            <hr />
            <p className="p-small" style={{ marginBottom: 0 }}>
              No Pryme ID yet? <a href={userPortalUrl('/enroll')}>Create one</a>. In sandbox this takes a minute with a test identity; then return here.
            </p>
          </aside>
        </div>
      </section>

      <section className="dv-section" aria-labelledby="dv-env-title">
        <h2 id="dv-env-title">Environments</h2>
        <p className="p-lead">Sandbox and production are separate deployments with separate trust roots, organizations, services and keys. Switch between them in the top bar.</p>
        <div className="dv-envs">
          <article className="dv-env" aria-labelledby="dv-env-sandbox">
            <div className="dv-env-head">
              <h3 id="dv-env-sandbox">Sandbox</h3>
              <Badge tone="ok">Available now</Badge>
            </div>
            <ul>
              <li>Test identities only: Idura MitID test users and an offline test provider.</li>
              <li>Any HTTPS redirect URI, plus <code>http://localhost</code>. No domain verification.</li>
              <li>Sandbox evidence is labelled as such and is never valid as real verification.</li>
            </ul>
            {host(sandbox?.api) && <p className="p-small p-muted" style={{ margin: 0 }}>API <code className="p-mono-chip">{host(sandbox?.api)}</code> <LocalTag url={sandbox?.api ?? ''} /></p>}
          </article>
          <article className="dv-env" aria-labelledby="dv-env-production">
            <div className="dv-env-head">
              <h3 id="dv-env-production">Production</h3>
              <Badge tone={production?.available ? 'ok' : 'neutral'}>{production?.available ? 'Available' : 'Opening soon'}</Badge>
            </div>
            <ul>
              <li>Real people verified with national eID (MitID through Idura). Document and liveness verification through Didit is in preparation as a global fallback.</li>
              <li>Redirect URIs on domains your organization has verified by DNS.</li>
              <li>Production services accept production evidence only.</li>
            </ul>
            {host(production?.api) && <p className="p-small p-muted" style={{ margin: 0 }}>API <code className="p-mono-chip">{host(production?.api)}</code> <LocalTag url={production?.api ?? ''} /></p>}
          </article>
        </div>
      </section>

      <section className="dv-section" aria-labelledby="dv-cap-title">
        <h2 id="dv-cap-title">What Pryme provides</h2>
        <p className="p-lead">National eID identifies people well. It does not let agents act for them, record standing permissions or carry electronic delegations. Pryme adds that layer.</p>
        <div className="p-features">
          {CAPABILITIES.map((c) => (
            <div key={c.title} className="p-feature">
              <span className="p-feature-icon"><Icon name={c.icon} size={18} /></span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="dv-section" aria-labelledby="dv-how-title">
        <h2 id="dv-how-title">How integration works</h2>
        <p className="p-lead">From a new Pryme ID to an active sandbox service in four steps.</p>
        <ol className="p-howto">
          {STEPS.map((s) => (
            <li key={s.title}>
              <strong>{s.title}</strong>
              <span>{s.text}</span>
            </li>
          ))}
        </ol>
        <p className="p-small" style={{ marginTop: '1.5rem' }}>
          <Link to="/docs">Quickstart and protocol reference <Icon name="arrowRight" size={14} /></Link>
        </p>
      </section>
    </>
  );
}
